import { Link } from "@tanstack/react-router";
import { ApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Page, PageScroll } from "@/components/layout/Page";

/**
 * One place that turns an error into something a person can act on.
 *
 * The distinction that matters is between "your session expired" and "your
 * account is not in an organisation". Both arrive as a 4xx, and treating them
 * the same sends the second case round a sign-in loop that cannot succeed.
 */
export function ErrorScreen({ error }: { error: unknown }) {
  const api = error instanceof ApiError ? error : null;

  let title = "Something went wrong";
  let detail = error instanceof Error ? error.message : "An unexpected error occurred.";
  let hint: string | null = null;

  if (api?.needsInvitation) {
    title = "No access to this workspace";
    detail = api.message;
    hint = "An administrator can invite your address, or claim your email domain.";
  } else if (api?.status === 403) {
    title = "Not permitted";
    detail = api.message;
    hint = "Your role in this organisation does not allow it.";
  } else if (api?.isUnauthenticated) {
    title = "Your session has ended";
    detail = "Sign in again to continue.";
  } else if (api?.status === 404) {
    title = "Not found";
    detail = "That item does not exist, or is private to someone else.";
  } else if (api?.code === "upstream_timeout") {
    title = "The answer took too long";
    detail = api.message;
    hint = "The question was saved. Opening it from Previous Questions may show it finished.";
  }

  return (
    <Page>
      <PageScroll className="pt-16">
        <div className="mx-auto max-w-xl text-center">
          <h1 className="erika-serif mb-2 text-2xl text-chrome">{title}</h1>
          <p className="mb-2 text-sm text-chrome/75">{detail}</p>
          {hint && <p className="mb-6 text-xs text-chrome/50">{hint}</p>}
          <div className="mt-6">
            <Link to="/ask">
              <Button variant="secondary">Back to Ask</Button>
            </Link>
          </div>
        </div>
      </PageScroll>
    </Page>
  );
}
